import { BaseFeature } from '../../core/base-feature.js';

/**
 * MercyFeature - Frontend window into the desktop's safeword / mercy handle
 *
 * The desktop owns everything. From here the partner can only:
 *   - read whether a mercy freeze is active (and when it fired), and
 *   - acknowledge the alert so the persistent banner can be dismissed.
 *
 * The partner CANNOT trigger, configure, or — most importantly — RESUME a mercy
 * freeze. Resume exists only on the desktop's own overlay. Status arrives as
 * 'status_update' pushes and as mercy.* Sacred Timeline events.
 */
export class MercyFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.pendingRequests = new Map();
        this.currentStatus = null;
    }

    static getMetadata() {
        return {
            id: 'mercy',
            name: 'Mercy Handle',
            version: 'v1.0',
            capabilities: ['get_status', 'acknowledge'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Mercy] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'status_update':
                this.currentStatus = payload;
                this.emit('statusUpdate', payload);
                break;

            case 'get_status_response':
            case 'acknowledge_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this.pendingRequests.has(originalId)) {
                    const { resolve } = this.pendingRequests.get(originalId);
                    this.pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Mercy] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this.pendingRequests.set(messageId, {
                resolve: (result) => { clearTimeout(timeout); resolve(result); },
                reject
            });
        });
    }

    getStatus() {
        return this._sendRequest('get_status');
    }

    /** Dismiss the alert. This never lifts the freeze — only the Subject can. */
    acknowledge() {
        return this._sendRequest('acknowledge');
    }

    async cleanup() {
        for (const [, { reject }] of this.pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this.pendingRequests.clear();
    }
}
